import { Injectable } from '@angular/core'
import { OAuth } from 'models/oauth'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import {
  HubtypeApiService,
  NO_VERSIONED_API,
} from 'src/app/services/hubtype-api/hubtype-api.service'
import {
  TwoFactorConfigResponse,
  TwoFactorRecoveryCodesResponse,
  TwoFactorSecret,
} from '../models/two-factor.model'

@Injectable({ providedIn: 'root' })
export class TwoFAService {
  constructor(private apiService: HubtypeApiService) {}

  confirmLogin(code: string): Observable<OAuth> {
    return this.apiService
      .post(
        '/_auth/app/v1/auth/2fa/authenticate',
        { code },
        null,
        NO_VERSIONED_API,
        false
      )
      .pipe(map(({ meta }) => meta))
  }

  hasTwoFactorAuthEnabled() {
    return this.apiService
      .get(
        '/_auth/app/v1/account/authenticators',
        null,
        NO_VERSIONED_API,
        false
      )
      .pipe(
        map(({ data }) => ({
          relogin: false,
          two_factor_enabled: data.length > 0,
        })),
        catchError(({ error }) => {
          return of({
            relogin: this.shouldRelogin(error),
            two_factor_enabled: false,
          })
        })
      )
  }

  generateTwoFactorSecret(): Observable<TwoFactorSecret> {
    return this.apiService
      .get(
        '/_auth/app/v1/account/authenticators/totp',
        null,
        NO_VERSIONED_API,
        false
      )
      .pipe(
        catchError(({ error }) => {
          if (error.status === 404) {
            return of(error.meta)
          }

          return of({
            secret: '',
            totp_url: '',
            reauthenticate: this.shouldReauthenticate(error),
            relogin: this.shouldRelogin(error),
          })
        })
      )
  }

  activateTwoFactor(code: string): Observable<TwoFactorConfigResponse> {
    return this.apiService
      .post(
        '/_auth/app/v1/account/authenticators/totp',
        { code },
        null,
        NO_VERSIONED_API,
        false
      )
      .pipe(
        map(({ status }) => ({
          activated: status === 200,
        })),
        catchError(({ error }) => {
          return of({
            is_code_invalid: error.status === 400,
            reauthenticate: this.shouldReauthenticate(error),
            relogin: this.shouldRelogin(error),
            activated: false,
          })
        })
      )
  }

  listRecoveryCodes(): Observable<TwoFactorRecoveryCodesResponse> {
    return this.apiService
      .get(
        '/_auth/app/v1/account/authenticators/recovery-codes',
        null,
        NO_VERSIONED_API,
        false
      )
      .pipe(
        map(({ data }) => ({ unused_codes: data.unused_codes })),
        catchError(({ error }) => {
          return of({
            reauthenticate: this.shouldReauthenticate(error),
            relogin: this.shouldRelogin(error),
            unused_codes: [],
          })
        })
      )
  }

  regenerateRecoveryCodes(): Observable<TwoFactorRecoveryCodesResponse> {
    return this.apiService
      .post(
        '/_auth/app/v1/account/authenticators/recovery-codes',
        null,
        null,
        NO_VERSIONED_API,
        false
      )
      .pipe(
        map(({ data }) => ({
          unused_codes: data.unused_codes,
        })),
        catchError(({ error }) => {
          return of({
            reauthenticate: this.shouldReauthenticate(error),
            relogin: this.shouldRelogin(error),
            unused_codes: [],
          })
        })
      )
  }

  deactivateTwoFactor(): Observable<TwoFactorConfigResponse> {
    return this.apiService
      .delete(
        '/_auth/app/v1/account/authenticators/totp',
        null,
        null,
        NO_VERSIONED_API,
        false
      )
      .pipe(
        map(({ status }) => ({
          activated: status !== 200,
        })),
        catchError(({ error }) => {
          return of({
            reauthenticate: this.shouldReauthenticate(error),
            relogin: this.shouldRelogin(error),
            activated: true,
          })
        })
      )
  }

  reauthenticate(password: string): Observable<TwoFactorConfigResponse> {
    return this.apiService
      .post(
        '/_auth/app/v1/auth/reauthenticate',
        { password },
        null,
        NO_VERSIONED_API,
        false
      )
      .pipe(
        map(() => ({
          reauthenticate: false,
          is_code_invalid: false,
        })),
        catchError(({ error }) => {
          return of({
            reauthenticate: true,
            is_code_invalid: error.status === 400,
          })
        })
      )
  }

  private shouldReauthenticate(error: any) {
    return !!error.data?.flows?.find(flow => flow.id === 'reauthenticate')
  }

  private shouldRelogin(error: any) {
    return !!error.data?.flows?.find(flow => flow.id === 'login')
  }
}
